import { gsap } from "gsap"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"
import { utilities } from "@/scripts/common/utilities"
import bannerCardTpl from "@/scripts/common/templates/banner-card.template"
import CardCarousel from "@/scripts/carousels/card-carousel"
import settings from "@/scripts/common/settings"
import {refreshAllControllers} from "@/scripts/common/api/scrolltrigger.services"

gsap.registerPlugin(ScrollToPlugin)

export const Accordion = (() => {
    // Accordion Toggler
    class AccordionToggler {
        constructor(container, options) {
            this.$accordion = container
            this.$accordionTrigger = this.$accordion.querySelector('.accordion__trigger') || this.$accordion
            this.$accordionPanel = this.$accordion.querySelector('.accordion__panel') || document.querySelector(this.$accordionTrigger.getAttribute('href'))
            this.$accordionIconOpen = this.$accordion.querySelector('.accordion__icon--open')
            this.$accordionIconClose = this.$accordion.querySelector('.accordion__icon--close')
            this.accordionPanelPersistent = !!this.$accordionPanel.dataset.accordionPanelPersistent || false
            this.triggerCloseText = this.$accordionTrigger.dataset.accordionCloseText || ''
            this.triggerActiveText = this.triggerCloseText ? this.$accordionTrigger.innerHTML : ''
            this.isMobile = window.innerWidth < settings.breakpoints.mobile
            this.options = Object.assign({
                ease: 'power2.inOut'
            }, options)
            this.active = !!this.$accordion.dataset.active || false
            this.events = []
            this.tl = null
            this.init()

        }

        init() {
            this.createEvents()
            this.updateUI()
            this.addListener()
            this.$accordion.getAccordion = () => this
            this.$accordionPanel.getAccordion = () => this
        }

        updateUI() {
            this.toggleClasses()
            if (this.triggerCloseText) this.toggleTriggerHtml()
            this.togglePanelAnim()
        }

        toggleClasses() {
            if (this.active) {
                this.$accordion.classList.add('accordion--active')
                this.$accordion.setAttribute('data-active', '')
            } else {
                this.$accordion.classList.remove('accordion--active')
                this.$accordion.removeAttribute('data-active', '')
            }
        }

        toggleTriggerHtml() {
            this.$accordionTrigger.innerHTML = this.active ? this.triggerCloseText : this.triggerActiveText
        }

        togglePanelAnim() {
            this.tl = null
            this.tl = new gsap.timeline()
                .to(this.$accordionPanel, {
                    duration: 0.4,
                    overwrite: 'all',
                    ease: this.options.ease,
                    height: this.active ? 'auto' : 0
                })
            if (this.$accordionIconOpen)
                this.tl.to(this.$accordionIconOpen, {
                    duration: 0.2,
                    overwrite: 'all',
                    ease: this.options.ease,
                    autoAlpha: this.active ? 0 : 1,
                    display: this.active ? 'none' : 'block',
                }, this.active ? 0 : 0.2)
            if (this.$accordionIconClose)
                this.tl.to(this.$accordionIconClose, {
                    duration: 0.2,
                    overwrite: 'all',
                    ease: this.options.ease,
                    autoAlpha: this.active ? 1 : 0,
                    display: this.active ? 'block' : 'none',
                }, !this.active ? 0 : 0.2)
            
            this.tl.add(() => {
                this.$accordion.dispatchEvent(this.active ? this.events['panelOpen'] : this.events['panelClose'])
                refreshAllControllers() // Scroll Trigger controller refresh after open
            })
        }

        scrollToPanel() {
            if (!this.tl || !this.active) return
            let $target = this.$accordionTrigger
            if(this.$accordionTrigger.closest(".accordion-group") && !this.isMobile) {
                $target = this.$accordionTrigger.closest(".accordion-group")
            }
            this.tl.eventCallback('onComplete', () => {
                gsap.to(window, {
                    duration: 0.4,
                    scrollTo: {
                        y: $target,
                        offsetY: 100
                    },
                    ease: 'power2.inOut'
                })
            })
        }

        getPanel() {
            return this.$accordionPanel
        }

        panelOpen() {
            if(this.active) return
            this.active = true
            this.updateUI()
            //this.$accordion.dispatchEvent(this.events['panelOpen'])
        }

        panelClose() {
            if(!this.active) return
            this.active = false
            this.updateUI()
            //this.$accordion.dispatchEvent(this.events['panelClose'])
        }

        createEvents() {
            this.events['panelOpen'] = new CustomEvent('panelOpen', {
                bubbles: true,
                detail: {
                    isActive: () => this.active
                }
            })

            this.events['panelClose'] = new CustomEvent('panelClose', {
                bubbles: true,
                detail: {
                    isActive: () => this.active
                }
            })
        }

        triggerClickHandler(e) {
            e.preventDefault()
            !this.active ? this.panelOpen() : this.panelClose()
            this.scrollToPanel() // only for click
        }

        addListener() {
            if(!this.accordionPanelPersistent)
                document.addEventListener('click', (event) => {
                    const isClickInside = this.$accordion.contains(event.target) || this.$accordionPanel.contains(event.target) || (this.$accordion === event.target)
                    if (!isClickInside) this.active && this.panelClose()
                })

            this.$accordionTrigger.addEventListener('click', (e) => this.triggerClickHandler(e))
        }
    }

    // Accordion group
    class AccordionGroup {
        constructor(container, options) {
            this.$accordionGroup = container
            this.$accordionBody = this.$accordionGroup.querySelector('.accordion-group__body')
            this.$accordionMedia = this.$accordionGroup.querySelector('.accordion-group__media')
            this.$accordionItems = gsap.utils.toArray(this.$accordionGroup.querySelectorAll('.accordion'))
            this.$featuredCarousel = null
            this.isMobile = window.innerWidth < settings.breakpoints.mobile
            this.activeAccordion = this.$accordionItems.findIndex($item => $item.hasAttribute("data-active")) !== -1 ? this.$accordionItems.findIndex($item => $item.hasAttribute("data-active")) : 0
            this.carouselItems = []

            this.options = Object.assign({
                persistent: true
            }, options)

            this.init()
        }

        init() {
            this.getAccordionData()
            if(this.isMobile) this._createIndividualAccordionCarousels()
            if(!this.isMobile) this._createFeaturedCarousel()
            this.addListeners()
        }

        getAccordionData() {
            this.$accordionItems.forEach(($item, index) => {
                const carouselImages = $item.dataset.carousel ? JSON.parse($item.dataset.carousel) : []

                this.carouselItems.push({
                    item: index,
                    carouselImages,
                    pagination: !!$item.dataset.carouselPagination || false
                })
            })
        }

        // Carousels
        // Single accordion carousels - mobile
        _createIndividualAccordionCarousels() {
            this.$accordionItems.forEach(($item, index) => {
                const data = $item.dataset.carousel ? JSON.parse($item.dataset.carousel) : []
                if (data.length) {
                    const $panelMedia = document.createElement('div')
                    $panelMedia.classList.add('accordion__panel-media')
                    $panelMedia.appendChild(this.buildCarousel(data))
                    $item.querySelector('.accordion__panel').append($panelMedia)
                    new CardCarousel($item.querySelector('.card-carousel__list'), {
                        pagination: this.carouselItems[index].pagination,
                        //adaptiveHeight: true
                    })
                }
            })
        }

        // Featured carousels - desktop
        _createFeaturedCarousel() {
            const items = this.carouselItems[this.activeAccordion]
            if (!this.$accordionMedia) {
                const $panelMedia = document.createElement('div')
                $panelMedia.classList.add('accordion-group__media')
                this.$accordionGroup.appendChild($panelMedia)
                this.$accordionMedia = $panelMedia
            }

            this.$featuredCarousel = this.buildCarousel(items.carouselImages)
            this.$accordionMedia.appendChild(this.$featuredCarousel)
            gsap.from(this.$featuredCarousel, { autoAlpha: 0 })

            new CardCarousel(this.$featuredCarousel.querySelector('.card-carousel__list'), {
                pagination: this.carouselItems[this.activeAccordion].pagination,
                //adaptiveHeight: true
            })


        }

        buildCarousel(carouselItems) {

            const $carousel = document.createElement('div')
            $carousel.classList.add('card-carousel', 'card-carousel--gutter', 'card-carousel--overlay-controls')
            $carousel.innerHTML = `<div class="card-carousel__list" data-carousel-type="single" data-items-to-show="1" data-lazyload="ondemand"></div>`

            carouselItems.forEach((item) => {
                const $carouselList = $carousel.querySelector('.card-carousel__list')
                $carouselList.appendChild(this.buildCarouselItem(item))
            })

            return $carousel
        }

        buildCarouselItem(item) {

            const $carouselItem = document.createElement('div')
            $carouselItem.classList.add('card-carousel__item')
            $carouselItem.setAttribute('data-pagination-label', item.title)
            const $carouselBanner = utilities.createNodeParsing(bannerCardTpl())
            $carouselBanner.classList.add('banner--native')
            const $carouselBannerBg = $carouselBanner.querySelector('.bg-img')
            $carouselBannerBg.setAttribute('data-img', true)
            $carouselBannerBg.innerHTML = `<img class="bg-img__img" data-lazy=${item.img} alt="" title="">`

            $carouselItem.innerHTML = `<div class="card-carousel__cell"></div>`
            const $carouselCell = $carouselItem.querySelector('.card-carousel__cell')
            $carouselCell.appendChild($carouselBanner)

            return $carouselItem
        }

        // FEatured carousel switching
        switchFeaturedCarousel() {
            if (this.$featuredCarousel) {
                this.$accordionGroup.classList.add('accordion-group--featured-tweening')
                gsap.to(this.$featuredCarousel, {
                    autoAlpha: 0,
                    display: 'none',
                    overwrite: 'all',
                    onComplete: () => {
                        this.$featuredCarousel.remove()
                        this._createFeaturedCarousel()
                        this.$accordionGroup.classList.remove('accordion-group--featured-tweening')
                    }
                })
            }

        }
        // Callbacks
        selectActive(val) {
            if (this.activeAccordion === val) return
            this.activeAccordion = val
            this.closeOtherPanels()
            
            this.switchFeaturedCarousel()
        }

        closeOtherPanels() {
            this.$accordionItems.forEach(($item, index) => index !== this.activeAccordion && $item.getAccordion().panelClose())
        }

        // Toggle others animation

        selectedToggleAnimation(open) {
            const $itemsPassive = gsap.utils.toArray(this.$accordionItems).filter((item, index) => index !== this.activeAccordion)
            const $itemActive = this.$accordionItems[this.activeAccordion]
            const tl = gsap.timeline()
            
            gsap.set(this.$accordionGroup, {
                perspective: 300
            })

            gsap.set(this.$accordionItems, {
                transformStyle:"preserve-3d"
            })

            if(open) {
                tl.to($itemsPassive, {
                    height: 0,
                    autoAlpha: 0,
                    scale: 0,
                    duration: 0.6,
                    stagger: 0.1,
                    ease: "power2.inOut"
                })

                return
            }

            tl.to($itemsPassive, {
                height: 'auto',
                autoAlpha: 1,
                scale: 1,
                duration: 0.6,
                stagger: 0.1,
                ease: "power2.inOut"
            })
        }

        addListeners() {
            this.$accordionItems.forEach(($item, index) => {
                $item.querySelector(`.accordion__trigger`).addEventListener('click', () => {
                    this.selectActive(index)
                    this.selectedToggleAnimation($item.getAccordion().active)
                })
                //$item.addEventListener('panelOpen', () => this.selectActive(index))
                
            })
        }
    }

    return {
        AccordionToggler,
        AccordionGroup
    }
})()

export default Accordion